/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/**
 * Refiner options type.
 */
export enum RefinerOptionsType {
    SingleSelect = 0,
    MultiSelect = 1
}

/**
 * Refiner item type.
 */
export enum RefinerItemType {
    Check = 0,
    DateRange = 1
}

/**
 * IRefinerItemData interface.
 */
export interface IRefinerItemData {
    refinerItemId: number; // must be unique within the refiner group (sourceTypeValue)
    sourceTypeValue: number;
    itemType: RefinerItemType;
    label: string;
    value?: string;
    isChecked?: boolean;
    minLabel?: string;
    maxLabel?: string;
    minValue?: string;
    maxValue?: string;
}

/**
 * IRefinerOptionsData interface.
 */
export interface IRefinerOptionsData {
    refinerOptionsId: number; // must be unique within the refiner (sourceTypeValue)
    sourceTypeValue: number;
    name?: string;
    typeValue: RefinerOptionsType;
    isExpanded?: boolean;
    refinerItems: IRefinerItemData[];
    selectedRefinerItems: IRefinerItemData[];
}

/**
 * IRefinerData interface.
 */
export interface IRefinerData {
    refinerOptions: IRefinerOptionsData[];
}

/**
 * IRefinerItemToggleNotification interface.
 */
export interface IRefinerItemToggleNotification {
    refinerOptionsData: IRefinerOptionsData;
    refinerItemData: IRefinerItemData;
    isSelecting: boolean;
    rangeStart?: string;
    rangeEnd?: string;
}

/**
 * Get updated refinement criteria.
 * @param itemToggleNotification - RefineItemToggleNotification object.
 * @param currentRefinementCriteria - Array of IRefineItemData.
 * @returns - Array of IRefineItemData.
 */
export function getUpdatedRefinementCriteria(
    itemToggleNotification: IRefinerItemToggleNotification,
    currentRefinementCriteria: IRefinerItemData[]
): IRefinerItemData[] {
    const updatedRefinementCriteria: IRefinerItemData[] = [];
    let isToggledItemFound = false;

    // Keep all the existing refinement criteria, except the given item toggle notification if it is not selected.
    for (const selectedCriterion of currentRefinementCriteria) {
        if (isMatchingRefinementCriterion(itemToggleNotification.refinerItemData, selectedCriterion)) {
            isToggledItemFound = true;
            if (itemToggleNotification.isSelecting) {
                const next = {
                    ...selectedCriterion,
                    minValue:
                        itemToggleNotification.rangeStart !== undefined
                            ? `${itemToggleNotification.rangeStart}`
                            : selectedCriterion.minValue,
                    maxValue:
                        itemToggleNotification.rangeEnd !== undefined ? `${itemToggleNotification.rangeEnd}` : selectedCriterion.maxValue
                };
                updatedRefinementCriteria.push(next);
            } // Else the item is being de-selected, so omit it from the refinement criteria
        } else {
            // Keep existing criterion because it is not in the item toggle notification
            updatedRefinementCriteria.push(selectedCriterion);
        }
    }

    if (!isToggledItemFound) {
        const next = {
            ...itemToggleNotification.refinerItemData
        };

        updatedRefinementCriteria.push(next);

        // If single select, then deselect any others in the parent refiner group
        if (itemToggleNotification.refinerOptionsData.typeValue === RefinerOptionsType.SingleSelect) {
            for (const child of itemToggleNotification.refinerOptionsData.refinerItems) {
                if (!(child.refinerItemId === next.refinerItemId && child.label === next.label)) {
                    const matchingIndex = updatedRefinementCriteria.findIndex(criterion => isMatchingRefinementCriterion(child, criterion));
                    // eslint-disable-next-line max-depth -- Condition required.
                    if (matchingIndex > -1) {
                        updatedRefinementCriteria.splice(matchingIndex, 1);
                    }
                }
            }
        }
    }

    return updatedRefinementCriteria;
}

/**
 * Find the refinement criterion associated with this given refiner value.
 * @param refinerValue - Refiner value to match.
 * @param refinementCriterion - Selected refinement criteria.
 * @returns - Boolean.
 */
export function isMatchingRefinementCriterion(refinerValue: IRefinerItemData, refinementCriterion: IRefinerItemData): boolean {
    return (
        refinementCriterion.refinerItemId === refinerValue.refinerItemId &&
        refinementCriterion.label === refinerValue.label &&
        refinerValue.sourceTypeValue === refinementCriterion.sourceTypeValue
    );
}
